import React, { useState, useEffect } from 'react';
import DisclaimerGate from './DisclaimerGate';
import { getAuth } from 'firebase/auth';
import { fetchRiskAssessmentOverride } from '../../services/riskAssessmentOverrideService';
import { normalizeProfileType } from './utils/cryptoCalculationUtils';

// You'll need to import these from your RiskAssessment app:
// import questions from './data/crypto-questions.js';
// import { calculateResults } from './utils/cryptoCalculationUtils.js';
// import QuestionSlider from './components/QuestionSlider';
// import ProgressBar from './components/ProgressBar';
// import CryptoReportView from './components/CryptoReportView';
// import logo from './images/arc-logo.png'

/**
 * RiskAssessmentComponent - Investor risk profile assessment
 * 
 * Props:
 *  - questions: array of questions for risk assessment
 *  - calculateResults: function to calculate risk assessment results
 *  - QuestionSlider: component for question slider
 *  - ProgressBar: component for progress bar
 *  - CryptoReportView: component for detailed report view
 *  - logo: logo image for risk assessment
 *  - riskAssessmentData: saved assessment data from parent
 *  - setRiskAssessmentData: function to update parent's assessment data
 */
const RiskAssessmentComponent = ({ 
  questions, 
  calculateResults, 
  QuestionSlider, 
  ProgressBar, 
  CryptoReportView, 
  logo,
  riskAssessmentData,
  setRiskAssessmentData
}) => {
  // State for tracking current question, answers, and results
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    riskAssessmentData?.currentQuestionIndex || 0
  );
  const [answers, setAnswers] = useState(riskAssessmentData?.answers || {});
  const [results, setResults] = useState(riskAssessmentData?.results || null);
  const [showResults, setShowResults] = useState(riskAssessmentData?.showResults || false);
  const [showReport, setShowReport] = useState(riskAssessmentData?.showReport || false);

  // Disclaimer gate — must be acknowledged before the assessment can be
  // accessed. The DisclaimerGate component handles its own Firestore
  // lookup on mount (and one-time migration from legacy localStorage),
  // so we start in the "gate-showing" state and let the gate decide
  // whether to pass through.
  const [disclaimerAck, setDisclaimerAck] = useState(null);

  // Per-user scoring override. Read once on mount. Defaults to
  // safe-by-default (honorStatedProfile = false) so safety rails fire
  // for any user without an explicit override doc. See
  // src/services/riskAssessmentOverrideService.js for details.
  const [overrideFlags, setOverrideFlags] = useState({
    honorStatedProfile: false,
  });
  // Track whether override fetch has completed so the migration effect
  // below doesn't run with stale defaults (which would silently override
  // the user's actual flag for one render).
  const [overrideLoaded, setOverrideLoaded] = useState(false);
  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) {
      setOverrideLoaded(true);
      return;
    }
    let cancelled = false;
    fetchRiskAssessmentOverride(uid).then((flags) => {
      if (cancelled) return;
      setOverrideFlags(flags);
      setOverrideLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Always-recompute on load ────────────────────────────────────
  //
  // History: this hook started as a narrow "stale tier name" migration
  // (Ultra-Conservative → Conservative, 2026-06-02). It only re-ran
  // when normalizeProfileType changed the bucket label.
  //
  // Updated 2026-06-22: now ALWAYS re-runs calculateResults() when
  // saved answers exist. Reason: the previous narrow check missed
  // cases where the bucket NAME stayed the same but the
  // PROFILE_THRESHOLDS or scoring formula in cryptoCalculationUtils.js
  // changed. Users would stay frozen in their old bucket even when
  // the engine would now produce a different one for identical
  // answers (e.g. the 2026-06-20 threshold re-calibration left
  // existing "Growth-Oriented" users showing Growth-Oriented when
  // their answers now actually score to Moderate).
  //
  // Safe to always re-run: calculateResults is pure + deterministic
  // (same answers + same code → same output). The only thing that
  // changes is when the engine code changes — which is exactly when
  // we want to re-bucket. Cost is a few ms per page load.
  //
  // Runs once on mount after override flags load. The set updates
  // parent state via the normal setRiskAssessmentData flow below.
  useEffect(() => {
    if (!overrideLoaded) return;
    if (!answers || Object.keys(answers).length === 0) return;

    const freshResults = calculateResults(answers, questions, {
      honorStatedProfile: overrideFlags.honorStatedProfile,
    });

    // Skip the state update if nothing actually changed — prevents
    // a needless re-render and the "saved profile" doc from being
    // re-written on every page load for users whose bucket already
    // matches current methodology.
    const prevType = results?.riskProfile?.type;
    const prevScore = results?.totalScore;
    const nextType = freshResults?.riskProfile?.type;
    const nextScore = freshResults?.totalScore;
    if (prevType === nextType && prevScore === nextScore) return;

    // eslint-disable-next-line no-console
    console.info(
      `[RiskAssessment] re-bucketed under current engine: "${prevType}" (${prevScore}) -> "${nextType}" (${nextScore})`
    );
    setResults(freshResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideLoaded]);

  // Update parent state whenever local state changes
  useEffect(() => {
    if (setRiskAssessmentData) {
      setRiskAssessmentData({
        currentQuestionIndex,
        answers,
        results,
        showResults,
        showReport
      });
    }
  }, [currentQuestionIndex, answers, results, showResults, showReport, setRiskAssessmentData]);

  // Handle slider input from questions
  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  // Navigate to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Scroll to the top of the risk assessment component instead of the whole page
      // You can remove this line entirely if you don't want any scrolling
    } else {
      // Pass per-user scoring options. honorStatedProfile defaults to
      // false (safety rails enforced), but a per-user override doc in
      // Firestore can flip it to true so the user's score-derived
      // profile becomes canonical and safety concerns appear as
      // advisory banners instead of silent demotions.
      const calculatedResults = calculateResults(answers, questions, {
        honorStatedProfile: overrideFlags.honorStatedProfile,
      });
      setResults(calculatedResults);
      setShowResults(true);
    }
  };

  // Navigate to previous question
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Scroll to the top of the risk assessment component instead of the whole page
      // You can remove this line entirely if you don't want any scrolling
    }
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const currentQuestion = questions[currentQuestionIndex];
    return answers[currentQuestion.id] !== undefined;
  };

  // Restart the assessment
  const restartAssessment = () => {
    setAnswers({});
    setResults(null);
    setShowResults(false);
    setCurrentQuestionIndex(0);
    setShowReport(false);
    // Removed window.scrollTo(0, 0) to prevent page scroll
  };

  // Toggle report view
  const toggleReport = () => {
    setShowReport(!showReport);
  };
  
  // Current question
  const currentQuestion = questions[currentQuestionIndex];

  // Gate: until the user acknowledges the disclaimer, they can't see ANY
  // part of the assessment — not the questions, not the report.
  if (!disclaimerAck) {
    return <DisclaimerGate onAcknowledge={(record) => setDisclaimerAck(record)} />;
  }

  return (
    <div className="bg-arc-bg py-8 px-4 rounded-lg">
      <div className="max-w-3xl mx-auto">
        <header className="text-center mb-8">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src={logo} 
              alt="ARC Logo" 
              className="h-16 w-auto mx-auto mb-4"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-arc-ink">Investor Risk Profile</h1>
          <p className="text-arc-muted mt-2">Discover your investor risk tolerance and see an illustrative allocation framework for your profile.</p>
        </header>
        
        {!showResults ? (
          <div className="bg-arc-surface rounded-lg shadow-xl border border-slate-700 p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-arc-gold">
                Question {currentQuestionIndex + 1} of {questions.length}
              </h2>
              <div className="text-sm text-arc-muted mb-4">
                Category: {currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1)}
              </div>
              
              {/* Progress bar */}
              <ProgressBar 
                currentIndex={currentQuestionIndex} 
                totalQuestions={questions.length} 
              />
            </div>
            
            {/* Question slider */}
            <QuestionSlider 
              question={currentQuestion} 
              value={answers[currentQuestion.id]}
              onChange={handleAnswerChange}
            />
            
            {/* Navigation buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
                className={`px-6 py-2 rounded transition-all ${
                  currentQuestionIndex === 0 
                    ? 'bg-white/10 text-arc-muted cursor-not-allowed' 
                    : 'bg-arc-elevated text-arc-ink hover:bg-white/10 border border-slate-600'
                }`}
              >
                Previous
              </button>
              
              <button
                onClick={nextQuestion}
                disabled={!isCurrentQuestionAnswered()}
                className={`px-6 py-2 rounded transition-all ${
                  isCurrentQuestionAnswered() 
                    ? 'bg-arc-gold text-white hover:bg-arc-gold shadow-lg shadow-blue-600/25' 
                    : 'bg-white/10 text-arc-muted cursor-not-allowed'
                }`}
              >
                {currentQuestionIndex === questions.length - 1 ? 'See Results' : 'Next'}
              </button>
            </div>
          </div>
        ) : (
          <div className="animate-fadeIn">
            {!showReport ? (
              <div className="bg-arc-surface rounded-lg shadow-xl border border-slate-700 p-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-arc-ink">Your Crypto Risk Profile</h2>
                  <div className="mt-4 inline-block bg-blue-900/50 border border-blue-600 px-4 py-2 rounded-full">
                    <span className="text-2xl font-semibold text-arc-gold">{results.riskProfile.type}</span>
                  </div>
                  <p className="mt-4 text-arc-muted max-w-2xl mx-auto">{results.riskProfile.description}</p>
                </div>
                
                {/* Vault allocation — explicit 7-vault breakdown + cash */}
                <div className="mb-8 bg-arc-elevated border border-arc-line p-5 rounded-2xl">
                  <h3 className="text-lg font-medium text-arc-ink mb-4">Illustrative Vault Allocation</h3>
                  {(() => {
                    const va = results.vaultAllocation || {
                      leoBTC: 0, leoETH: 0, gemBTC: 0, gemini: 0,
                      scorpioV: 0, gemETH: 0, gemSOL: 0, cash: 0,
                    };
                    // Each row: label, key in vaultAllocation, risk badge, swatch color
                    const rows = [
                      { label: "Leo BTC Bull Run", key: "leoBTC",   risk: "3/10", color: "#22c55e" },
                      { label: "Leo ETH",          key: "leoETH",   risk: "4/10", color: "#10b981" },
                      { label: "Gemini BTC",       key: "gemBTC",   risk: "5/10", color: "#3b82f6" },
                      { label: "Gemini",           key: "gemini",   risk: "5/10", color: "#6366f1" },
                      { label: "Scorpio V",        key: "scorpioV", risk: "7/10", color: "#f59e0b" },
                      { label: "Gemini ETH",       key: "gemETH",   risk: "8/10", color: "#ef4444" },
                      { label: "Gemini SOL",       key: "gemSOL",   risk: "8/10", color: "#dc2626" },
                      { label: "Cash / Stables",   key: "cash",     risk: "—",    color: "#a1a1aa" },
                    ];
                    return (
                      <div className="space-y-2">
                        {rows.map((r) => {
                          const pct = va[r.key] || 0;
                          return (
                            <div key={r.key} className="flex items-center gap-3">
                              <div
                                className="w-3 h-3 rounded-sm flex-shrink-0"
                                style={{ background: r.color }}
                              />
                              <div className="flex-1 min-w-0 flex items-baseline gap-2">
                                <span className="text-sm text-arc-ink truncate">{r.label}</span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-arc-dim">
                                  {r.risk}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="w-32 bg-white/[0.04] rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{ width: `${pct}%`, background: r.color }}
                                  />
                                </div>
                                <span className="text-sm num text-arc-ink w-10 text-right">
                                  {pct}%
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Safety override banner — fires when capacity caps the score-derived profile */}
                {results.composite?.capApplied && results.safetyOverrides?.length > 0 && (
                  <div className="mb-6 rounded-2xl border border-arc-deep/40 bg-arc-deep/10 p-4">
                    <p className="eyebrow mb-2 text-arc-light">Safety override applied</p>
                    <p className="text-sm text-arc-muted mb-2">
                      Your tolerance scored <strong className="text-arc-ink">{results.composite.scoreDerivedProfile}</strong>,
                      but your capacity caps you at <strong className="text-arc-gold">{results.composite.profile}</strong> to protect your financial stability.
                    </p>
                    <ul className="space-y-1.5 text-sm text-arc-muted">
                      {results.safetyOverrides.map((o, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-arc-light">•</span>
                          <span>{o.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Behavior mismatch warning */}
                {results.behaviorWarning && (
                  <div className="mb-6 rounded-2xl border border-arc-deep/40 bg-arc-deep/10 p-4">
                    <p className="eyebrow mb-2 text-arc-light">Behavior mismatch flagged</p>
                    <p className="text-sm text-arc-muted">{results.behaviorWarning.warning}</p>
                  </div>
                )}
                
                <div className="text-center space-x-4">
                  <button
                    onClick={toggleReport}
                    className="bg-arc-gold text-white px-6 py-2 rounded hover:bg-arc-gold transition-colors shadow-lg shadow-blue-600/25"
                  >
                    View Detailed Report
                  </button>
                  <button
                    onClick={restartAssessment}
                    className="bg-arc-elevated text-arc-ink px-6 py-2 rounded hover:bg-white/10 transition-colors border border-slate-600"
                  >
                    Retake Assessment
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <CryptoReportView results={results} />
                
                <div className="text-center mt-8">
                  <button
                    onClick={toggleReport}
                    className="bg-arc-elevated text-arc-ink px-6 py-2 rounded hover:bg-white/10 transition-colors border border-slate-600 mr-4"
                  >
                    Back to Summary
                  </button>
                  <button
                    onClick={restartAssessment}
                    className="bg-arc-gold text-white px-6 py-2 rounded hover:bg-arc-gold transition-colors shadow-lg shadow-blue-600/25"
                  >
                    Retake Assessment
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskAssessmentComponent;